/**
 * Consumption Reality Dashboard — Sprint 3 Step 3.3
 *
 * "Material Progress Reality" — first dashboard sourced from actual
 * physical consumption (not just reservation/approval).
 *
 * Endpoints:
 *   GET /api/pece/consumption/summary?days=N
 *   GET /api/pece/consumption/log
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Flame, RefreshCw, Loader2, TrendingUp, AlertOctagon,
  Package, Activity, CheckCircle2, XCircle,
} from 'lucide-react';

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };

const STATUS_PILL = {
  reserved:           { label: 'محجوز',                cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  partially_consumed: { label: 'استهلاك جزئي',         cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  fully_consumed:     { label: 'استهلاك كامل',         cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  consumption_error:  { label: 'خطأ استهلاك',         cls: 'bg-rose-50 text-rose-700 border-rose-300' },
  released:           { label: 'مُحرَّر',              cls: 'bg-gray-50 text-gray-700 border-gray-300' },
};

const ConsumptionReality = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [log, setLog] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const logUrl = statusFilter ?
          `/pece/consumption/log?status=${statusFilter}&limit=200` :
          `/pece/consumption/log?limit=200`;
        const [s, l] = await Promise.all([
          axiosInstance.get(`/pece/consumption/summary?days=${windowDays}`),
          axiosInstance.get(logUrl),
        ]);
        if (!cancelled) { setSummary(s.data); setLog(l.data); }
      } catch (e) {
        if (!cancelled) toast.error('فشل تحميل لوحة الاستهلاك');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [windowDays, statusFilter, reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="consumption-dashboard-page">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-orange-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                <Flame className="w-4 h-4" /> Material Progress Reality · Step 3.3
              </div>
              <h1 className="text-2xl font-bold text-gray-900">واقع الاستهلاك التشغيلي</h1>
              <p className="text-sm text-gray-500 mt-1">
                أول طبقة تعرض ما <strong>تم استهلاكه فعلياً</strong> — وليس فقط ما تم حجزه أو طلبه.
                هذه هي الحقيقة التشغيلية التي ستُغذّي PRS وCost Engine وPHI.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="consumption-window-select"
                value={windowDays}
                onChange={(e) => setWindowDays(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value={7}>آخر 7 أيام</option>
                <option value={30}>آخر 30 يوماً</option>
                <option value={90}>آخر 90 يوماً</option>
                <option value={365}>آخر سنة</option>
              </select>
              <button
                data-testid="consumption-refresh-btn"
                onClick={reload}
                className="px-4 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 flex items-center gap-1 font-semibold text-sm"
              >
                <RefreshCw className="w-4 h-4" /> تحديث
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="consumption-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
          </div>
        )}

        {!loading && summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="consumption-kpi-strip">
              <KPI testId="kpi-fulfillment" icon={<TrendingUp className="w-5 h-5" />}
                    label="نسبة الاستهلاك من الحجوزات"
                    value={fmtPct(summary.fulfillment_pct)}
                    cls="text-orange-700 bg-orange-50 border-orange-200" />
              <KPI testId="kpi-fully-consumed" icon={<CheckCircle2 className="w-5 h-5" />}
                    label="استهلاكات كاملة"
                    value={String(summary.totals?.fully_consumed || 0)}
                    cls="text-emerald-700 bg-emerald-50 border-emerald-200" />
              <KPI testId="kpi-partial" icon={<Activity className="w-5 h-5" />}
                    label="استهلاكات جزئية"
                    value={String(summary.totals?.partially_consumed || 0)}
                    cls="text-amber-700 bg-amber-50 border-amber-200" />
              <KPI testId="kpi-errors" icon={<AlertOctagon className="w-5 h-5" />}
                    label="أخطاء استهلاك"
                    value={String(summary.totals?.consumption_error || 0)}
                    sub={`عجز مخزني — يحتاج مراجعة`}
                    cls="text-rose-700 bg-rose-50 border-rose-200" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Section title="أكثر المواد استهلاكاً"
                        icon={<Package className="w-5 h-5 text-gray-500" />}
                        testId="consumption-top-materials">
                {summary.top_materials.length === 0 ? (
                  <Empty />
                ) : (
                  <ul className="divide-y" data-testid="top-materials-list">
                    {summary.top_materials.map((m) => (
                      <li key={m.material_name} className="px-4 py-2 flex justify-between items-center">
                        <span className="font-semibold">{m.material_name}</span>
                        <span className="font-mono text-sm text-orange-700">{m.qty_consumed}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <Section title="أكثر الأنشطة استهلاكاً"
                        icon={<Activity className="w-5 h-5 text-gray-500" />}
                        testId="consumption-top-activities">
                {summary.top_activities.length === 0 ? (
                  <Empty />
                ) : (
                  <ul className="divide-y" data-testid="top-activities-list">
                    {summary.top_activities.map((a) => (
                      <li key={a.activity_code} className="px-4 py-2 flex justify-between items-center">
                        <span className="font-mono text-sm">{a.activity_code}</span>
                        <span className="font-mono text-sm text-orange-700">{a.qty_consumed}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>

            <Section title="سجل الاستهلاكات الأخيرة"
                      icon={<Flame className="w-5 h-5 text-gray-500" />}
                      testId="consumption-log-section"
                      action={
                        <select
                          data-testid="consumption-status-filter"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded bg-white"
                        >
                          <option value="">كل الحالات</option>
                          {Object.entries(STATUS_PILL).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                      }>
              {log.length === 0 ? (
                <Empty msg="لا توجد استهلاكات بعد. قم بقبول MIR لتوليد أول حدث استهلاك." />
              ) : (
                <table className="w-full text-sm" data-testid="consumption-log-table">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-600 font-bold">
                    <tr>
                      <th className="px-3 py-2 text-right">المادة</th>
                      <th className="px-3 py-2 text-right">الحالة</th>
                      <th className="px-3 py-2 text-right">محجوز / مستهلك</th>
                      <th className="px-3 py-2 text-right">آخر استهلاك</th>
                      <th className="px-3 py-2 text-right">السلسلة</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {log.map((r) => {
                      const meta = STATUS_PILL[r.status] || STATUS_PILL.reserved;
                      return (
                        <tr key={r.id} data-testid={`consumption-row-${r.id}`}
                            className="hover:bg-orange-50/30">
                          <td className="px-3 py-2">
                            <div className="font-semibold">{r.material_name}</div>
                            {r.activity_code && (
                              <div className="text-[10px] font-mono text-gray-500">{r.activity_code}</div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded border ${meta.cls}`}>
                              {meta.label}
                            </span>
                            {r.status === 'consumption_error' && (
                              <XCircle className="inline w-3 h-3 ml-1 text-rose-500" />
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {r.qty} / <strong className={r.consumed_qty ? 'text-orange-700' : 'text-gray-400'}>{r.consumed_qty || 0}</strong>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {r.last_consumed_at ? fmtDate(r.last_consumed_at) : '—'}
                          </td>
                          <td className="px-3 py-2">
                            {r.correlation_id && (
                              <a href={`/ecp?correlation_id=${r.correlation_id}`}
                                  className="text-xs text-orange-600 underline">
                                ECP →
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Section>
          </>
        )}
      </div>
    </div>
  );
};

const KPI = ({ testId, icon, label, value, sub, cls }) => (
  <div data-testid={testId} className={`rounded-xl border p-4 ${cls}`}>
    <div className="flex items-center gap-2 text-xs uppercase font-bold opacity-80">
      {icon}{label}
    </div>
    <div className="text-2xl font-bold mt-2">{value}</div>
    {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
  </div>
);

const Section = ({ title, icon, children, testId, action }) => (
  <div className="bg-white rounded-xl border" data-testid={testId}>
    <div className="px-4 py-3 border-b flex items-center gap-2 justify-between">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-bold text-gray-800">{title}</h3>
      </div>
      {action}
    </div>
    <div>{children}</div>
  </div>
);

const Empty = ({ msg = "لا توجد بيانات." }) => (
  <div className="text-center py-8 text-gray-500 text-sm">{msg}</div>
);

export default ConsumptionReality;
